import { computed, useContext } from "@nuxtjs/composition-api";
import { getRouteFeature } from "~/config/pages/localization.config";
import { FEATURES } from "~/config/pages/features.config";

// Re-export FEATURES for convenience
export { FEATURES };

export default function useFeatures(context) {
  const { app } = context ?? useContext();

  // Get brand features from the injected $brand plugin
  const brandFeatures = computed(() => app.$brand?.features || []);

  /**
   * Check if a feature is enabled for the current brand
   * @param {string} feature - Feature constant from FEATURES (e.g., FEATURES.BLOG)
   * @returns {boolean} - True if feature is enabled
   */
  const hasFeature = (feature) => {
    if (process.env.NODE_ENV === "development" && !isValidFeature(feature)) {
      console.warn(
        `[useFeatures] Invalid feature "${feature}". Use FEATURES constants from ~/config/pages/features.config.js`
      );
    }
    return brandFeatures.value.includes(feature);
  };

  /**
   * Check if a route is accessible based on its feature requirement
   * @param {string} routeName - Route name (e.g., 'blogLandingPage')
   * @returns {boolean} - True if route is accessible
   */
  const hasRouteAccess = (routeName) => {
    const requiredFeature = getRouteFeature(routeName);

    // If no feature is required, route is always accessible
    if (!requiredFeature) {
      return true;
    }

    // Check if the brand has the required feature
    return hasFeature(requiredFeature);
  };

  function getAllFeatures() {
    return Object.values(FEATURES);
  }

  function isValidFeature(feature) {
    return getAllFeatures().includes(feature);
  }

  return {
    brandFeatures,
    hasFeature,
    hasRouteAccess,
    getAllFeatures,
    isValidFeature,
    FEATURES,
  };
}
